import { useCallback, useMemo } from 'react';
import type { Campaign } from '@/services/db/campaign/entity';
import { useCampaignsCachedForWorkspace } from '@/services/db/campaign/queries';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { generatePath } from '@/services/router/generate-path';
import { matchRouteParams } from '@/services/router/match-route';
import { normalizeJourneysPathname } from '@/services/product-taxonomy/routes';
import { useLocation } from '@/services/router/compat';
import type { Route } from '@/services/router';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import type { LocalCommand } from './local-commands';
import type { PermissionGuard } from './use-navigation-search';
import { matchesLocalCommand } from './utils/search-commands';
import {
  addRoutingCommand,
  getNumericRouteParamFromPathname,
  matchesCurrentWorkspaceRoute,
  type Navigate,
} from './routing-command-utils';

type BuildCampaignRoutingCommandsArgs = {
  query: string;
  workspaceId: number;
  pathname: string;
  campaignState?: Campaign['state'] | null;
  guard: PermissionGuard | null;
  navigate: Navigate;
  campaignNoun: string;
  campaignNounPlural: string;
  peoplePlural: string;
  useAutomationsPaths: boolean;
};

type UseCampaignRoutingCommandsDeps = {
  workspaceId: number;
  guard: PermissionGuard | null;
  navigate: Navigate;
};

const CAMPAIGN_DETAIL_ROUTE = '/workspaces/:workspaceId/journeys/campaigns/:campaignId' satisfies Route;
const CAMPAIGN_OVERVIEW_ROUTE = '/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/*';
const CAMPAIGN_SETUP_ROUTE = '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/*';
const CAMPAIGN_STATIC_ROUTES = [
  '/workspaces/:workspaceId/journeys/campaigns',
  '/workspaces/:workspaceId/journeys/campaigns/new',
  '/workspaces/:workspaceId/journeys/campaigns/welcome',
  '/workspaces/:workspaceId/journeys/campaigns/welcome/create',
] as const satisfies readonly Route[];
const MAX_TYPED_CAMPAIGN_ROUTING_COMMANDS = 4;

// The router registers a parallel `/journeys/automations` route tree used when the rename flag
// is on. Generated destinations are built from the type-safe `/journeys/campaigns` Route literals,
// then rewritten to the active variant here so they match the path the nav navigates to.
function toActiveJourneysPath(path: string, useAutomationsPaths: boolean): string {
  return useAutomationsPaths ? path.replace('/journeys/campaigns', '/journeys/automations') : path;
}

type CampaignShortcutSurface = 'setup' | 'overview' | 'unknown';

function matchesCampaignChildRoute({
  pathname,
  pattern,
  workspaceId,
  campaignId,
}: {
  pathname: string;
  pattern: string;
  workspaceId: number;
  campaignId: string;
}): boolean {
  const params = matchRouteParams(normalizeJourneysPathname(pathname), pattern, { end: false });
  const isCurrentWorkspace = params?.workspaceId === 'last' || params?.workspaceId === String(workspaceId);
  return isCurrentWorkspace && params?.campaignId === campaignId;
}

function getCampaignShortcutSurface({
  pathname,
  workspaceId,
  campaignId,
  campaignState,
}: {
  pathname: string;
  workspaceId: number;
  campaignId: string | null;
  campaignState?: Campaign['state'] | null;
}): CampaignShortcutSurface {
  if (campaignId == null) return 'unknown';

  if (matchesCampaignChildRoute({ pathname, pattern: CAMPAIGN_SETUP_ROUTE, workspaceId, campaignId })) {
    return 'setup';
  }

  if (matchesCampaignChildRoute({ pathname, pattern: CAMPAIGN_OVERVIEW_ROUTE, workspaceId, campaignId })) {
    return 'overview';
  }

  if (campaignState === 'draft') return 'setup';
  if (campaignState != null) return 'overview';

  return 'unknown';
}

function useCachedCampaignState(workspaceId: number, campaignId: string | null): Campaign['state'] | null {
  const selectCampaignState = useCallback(
    (campaigns: readonly Campaign[]) =>
      campaignId == null ? null : (campaigns.find((campaign) => campaign.id === campaignId)?.state ?? null),
    [campaignId],
  );

  return useCampaignsCachedForWorkspace<Campaign['state'] | null>(workspaceId, { select: selectCampaignState });
}

export function getCampaignIdFromPathname(pathname: string, workspaceId: number): string | null {
  return getNumericRouteParamFromPathname({
    pathname: normalizeJourneysPathname(pathname),
    workspaceId,
    route: CAMPAIGN_DETAIL_ROUTE,
    paramName: 'campaignId',
  });
}

export function isCampaignPathname(pathname: string, workspaceId: number): boolean {
  if (workspaceId === 0) return false;
  const normalizedPathname = normalizeJourneysPathname(pathname);
  if (getCampaignIdFromPathname(normalizedPathname, workspaceId) != null) return true;
  return CAMPAIGN_STATIC_ROUTES.some((route) => matchesCurrentWorkspaceRoute(normalizedPathname, route, workspaceId));
}

type CampaignRouteParams = { workspaceId: number; campaignId: string };

type CampaignOverviewCommandDef = {
  buildPath: (params: CampaignRouteParams) => string;
  idSuffix: string;
  label: string;
  description: string;
  keywords: string[];
};

function buildCampaignOverviewCommandDefs(noun: string, peoplePlural: string): readonly CampaignOverviewCommandDef[] {
  return [
    {
      buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview', p),
      idSuffix: 'overview',
      label: `Open ${noun} overview`,
      description: `Open the ${noun} overview`,
      keywords: ['campaign', 'overview', 'details'],
    },
    {
      buildPath: (p) =>
        generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/workflow/actions', p),
      idSuffix: 'campaign-workflow',
      label: `View ${noun} workflow`,
      description: 'View the workflow in read-only mode',
      keywords: ['campaign', 'workflow', 'actions'],
    },
    {
      buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/metrics', p),
      idSuffix: 'metrics',
      label: `View ${noun} metrics`,
      description: `Review ${noun} performance`,
      keywords: ['campaign', 'metrics', 'analytics', 'report'],
    },
    {
      buildPath: (p) =>
        generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/journey_metrics', p),
      idSuffix: 'journey-metrics',
      label: 'View journey metrics',
      description: 'Review journey-level performance',
      keywords: ['campaign', 'journey metrics', 'analytics', 'report'],
    },
    {
      buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/sent', p),
      idSuffix: 'sent',
      label: 'View sent deliveries',
      description: `See messages sent by this ${noun}`,
      keywords: ['campaign', 'sent', 'deliveries', 'messages'],
    },
    {
      buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/drafts', p),
      idSuffix: 'drafts',
      label: 'View drafts',
      description: 'Review draft deliveries',
      keywords: ['campaign', 'drafts', 'draft deliveries'],
    },
    {
      buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/tests', p),
      idSuffix: 'tests',
      label: 'View tests',
      description: 'Review test deliveries',
      keywords: ['campaign', 'tests', 'test deliveries'],
    },
    {
      buildPath: (p) => generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/journeys', p),
      idSuffix: 'journeys',
      label: 'View journeys',
      description: `See ${peoplePlural} currently in this ${noun}`,
      keywords: ['campaign', 'journeys', 'people'],
    },
    {
      buildPath: (p) =>
        generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview/form-submissions', p),
      idSuffix: 'form-submissions',
      label: 'View form submissions',
      description: `Review form submissions for this ${noun}`,
      keywords: ['campaign', 'form submissions', 'forms'],
    },
  ];
}

type AddCampaignDetailCommandsArgs = {
  commands: LocalCommand[];
  pathname: string;
  workspaceId: number;
  campaignId: string;
  shortcutSurface: CampaignShortcutSurface;
  canEditCampaigns: boolean;
  navigate: Navigate;
  campaignNoun: string;
  peoplePlural: string;
  useAutomationsPaths: boolean;
};

function addCampaignDetailCommands({
  commands,
  pathname,
  workspaceId,
  campaignId,
  shortcutSurface,
  canEditCampaigns,
  navigate,
  campaignNoun,
  peoplePlural,
  useAutomationsPaths,
}: AddCampaignDetailCommandsArgs): void {
  const campaignRouteParams = { workspaceId, campaignId };
  // Compare the current-page suppression against the same canonical path the destinations use,
  // so a command for the page you're on is omitted whether the browser is on the campaigns or
  // automations URL variant.
  const comparePathname = toActiveJourneysPath(normalizeJourneysPathname(pathname), useAutomationsPaths);

  if (canEditCampaigns && shortcutSurface !== 'overview') {
    addRoutingCommand(
      commands,
      comparePathname,
      navigate,
      toActiveJourneysPath(
        generatePath(
          '/workspaces/:workspaceId/journeys/campaigns/:campaignId/setup/workflow/actions',
          campaignRouteParams,
        ),
        useAutomationsPaths,
      ),
      {
        id: `campaign-routing:${campaignId}:workflow-builder`,
        label: 'Open workflow builder',
        description: `Edit the workflow for this draft ${campaignNoun}`,
        icon: 'campaigns',
        keywords: ['campaign', 'workflow', 'builder', 'edit', 'actions'],
      },
    );
  }

  if (shortcutSurface !== 'setup') {
    for (const { buildPath, idSuffix, label, description, keywords } of buildCampaignOverviewCommandDefs(
      campaignNoun,
      peoplePlural,
    )) {
      addRoutingCommand(
        commands,
        comparePathname,
        navigate,
        toActiveJourneysPath(buildPath(campaignRouteParams), useAutomationsPaths),
        {
          id: `campaign-routing:${campaignId}:${idSuffix}`,
          label,
          description,
          icon: 'campaigns',
          keywords,
        },
      );
    }
  }
}

export function buildCampaignRoutingCommands({
  query,
  workspaceId,
  pathname,
  campaignState,
  guard,
  navigate,
  campaignNoun,
  campaignNounPlural,
  peoplePlural,
  useAutomationsPaths,
}: BuildCampaignRoutingCommandsArgs): LocalCommand[] {
  if (workspaceId === 0) return [];
  if (isBuilderPlan(guard?.account)) return [];
  if (!isCampaignPathname(pathname, workspaceId)) return [];

  const trimmedQuery = query.trim();
  const isEmptyQuery = trimmedQuery.length === 0;

  const campaignId = getCampaignIdFromPathname(pathname, workspaceId);
  const canViewCampaigns = guard?.hasPermission('journeys/campaigns/view') === true;
  const canCreateCampaigns = guard?.hasPermission('journeys/campaigns/create') === true;
  const canEditCampaigns = guard?.hasPermission('journeys/campaigns/edit') === true;
  const commands: LocalCommand[] = [];
  const shortcutSurface = getCampaignShortcutSurface({ pathname, workspaceId, campaignId, campaignState });
  const comparePathname = toActiveJourneysPath(normalizeJourneysPathname(pathname), useAutomationsPaths);

  if (campaignId != null && canViewCampaigns) {
    addCampaignDetailCommands({
      commands,
      pathname,
      workspaceId,
      campaignId,
      shortcutSurface,
      canEditCampaigns,
      navigate,
      campaignNoun,
      peoplePlural,
      useAutomationsPaths,
    });
  }

  if (canCreateCampaigns && shortcutSurface !== 'setup') {
    addRoutingCommand(
      commands,
      comparePathname,
      navigate,
      toActiveJourneysPath(
        generatePath('/workspaces/:workspaceId/journeys/campaigns/new', { workspaceId }),
        useAutomationsPaths,
      ),
      {
        id: 'campaign-routing:create',
        label: `Create ${campaignNoun}`,
        description: `Start a new behavioral or date-triggered ${campaignNoun}`,
        icon: 'campaigns',
        keywords: ['campaign', 'campaigns', campaignNoun, campaignNounPlural, 'create', 'new'],
      },
    );
  }

  if (canViewCampaigns) {
    addRoutingCommand(
      commands,
      comparePathname,
      navigate,
      toActiveJourneysPath(
        generatePath('/workspaces/:workspaceId/journeys/campaigns', { workspaceId }),
        useAutomationsPaths,
      ),
      {
        id: 'campaign-routing:index',
        label: `Go to ${campaignNounPlural}`,
        description: `Browse all ${campaignNounPlural}`,
        icon: 'campaigns',
        keywords: ['campaign', 'campaigns', campaignNoun, campaignNounPlural, 'list'],
      },
    );
  }

  const matchingCommands = commands.filter(matchesLocalCommand(query));

  if (isEmptyQuery) return matchingCommands;

  return matchingCommands.slice(0, MAX_TYPED_CAMPAIGN_ROUTING_COMMANDS);
}

export function useCampaignRoutingCommands(query: string, deps: UseCampaignRoutingCommandsDeps): LocalCommand[] {
  const { pathname } = useLocation();
  const { workspaceId, guard, navigate } = deps;
  const campaignId = getCampaignIdFromPathname(pathname, workspaceId);
  const campaignState = useCachedCampaignState(workspaceId, campaignId);
  const taxonomy = useProductTaxonomy();
  const campaignNoun = taxonomy.term('campaigns.singular');
  const campaignNounPlural = taxonomy.term('campaigns.plural');
  const peoplePlural = taxonomy.term('people.plural');
  const useAutomationsPaths = taxonomy.variant === 'future';

  return useMemo(
    () =>
      buildCampaignRoutingCommands({
        query,
        workspaceId,
        pathname,
        campaignState,
        guard,
        navigate,
        campaignNoun,
        campaignNounPlural,
        peoplePlural,
        useAutomationsPaths,
      }),
    [
      query,
      workspaceId,
      pathname,
      campaignState,
      guard,
      navigate,
      campaignNoun,
      campaignNounPlural,
      peoplePlural,
      useAutomationsPaths,
    ],
  );
}
